import { useState, useEffect, useCallback } from 'react'
import { PackageService, PackageFilters } from '../services/packageService'
import { Package, PackagesResponse } from '../config/api'

export type { PackageFilters } from '../services/packageService'

interface UsePackagesOptions {
  initialFilters?: PackageFilters
  initialPage?: number
}

export const usePackages = (options: UsePackagesOptions = {}) => {
  const { initialFilters = {}, initialPage = 1 } = options
  const [packages, setPackages] = useState<Package[]>([])
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 6,
    has_next: false,
    has_prev: false,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<PackageFilters>(initialFilters)
  const [currentPage, setCurrentPage] = useState(1)

  const packageService = PackageService.getInstance()

  const fetchPackages = useCallback(async (page: number = 1, newFilters?: PackageFilters) => {
    try {
      setLoading(true)
      setError(null)
      
      const filtersToUse = newFilters || filters
      const response: PackagesResponse = await packageService.getPackages(page, filtersToUse)
      
      setPackages(response.packages)
      setPagination(response.pagination)
      setCurrentPage(page)
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch packages')
    } finally {
      setLoading(false)
    }
  }, [filters, packageService])

  // Initial load
  useEffect(() => {
    fetchPackages(initialPage, initialFilters)
  }, [])

  // Filter packages
  const applyFilters = useCallback((newFilters: PackageFilters) => {
    setFilters(newFilters)
    setCurrentPage(1)
    fetchPackages(1, newFilters)
  }, [fetchPackages])

  // Load more/next page
  const loadPage = useCallback((page: number) => {
    if (page >= 1 && page <= pagination.total_pages) {
      fetchPackages(page, filters)
    }
  }, [fetchPackages, pagination.total_pages, filters])

  // Helper functions
  const goToNextPage = useCallback(() => {
    if (pagination.has_next) {
      loadPage(currentPage + 1)
    }
  }, [loadPage, currentPage, pagination.has_next])

  const goToPrevPage = useCallback(() => {
    if (pagination.has_prev) {
      loadPage(currentPage - 1)
    }
  }, [loadPage, currentPage, pagination.has_prev])

  const refresh = useCallback(() => {
    fetchPackages(currentPage)
  }, [fetchPackages, currentPage])

  return {
    packages,
    pagination,
    loading,
    error,
    filters,
    currentPage,
    applyFilters,
    loadPage,
    goToNextPage,
    goToPrevPage,
    refresh,
  }
} 